// src/components/doctor/NextPatientCard.jsx
//
// "Next patient" on the doctor's home screen: the longest-waiting ARRIVED
// patient (GET /doctor/queue/next, 204 = nobody waiting) with how long they
// have waited, why they came, latest vitals + flags and any recorded allergy,
// and one button that calls them in (start-consultation) and opens the
// consultation. A compact waiting-room list (GET /clinic/queue) sits below.
// Refreshes every 30 s.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { getNextInQueue, getClinicQueue, startConsultation, getPatientAllergies } from '../../services/visitApi';
import { VITAL_FLAG_LABELS, formatWaiting, vitalsSummary } from '../../utils/consultation';

const REFRESH_MS = 30000;

export function VitalsLine({ vitals }) {
  const rows = vitalsSummary(vitals);
  if (!vitals || rows.length === 0) return <span className="card-row-meta">No vitals recorded yet</span>;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      {rows.map(([k, v]) => <span key={k} className="field-text"><span className="card-row-meta">{k} </span>{v}</span>)}
      {(vitals.flags || []).map(f => <span key={f} className="badge badge-critical">{VITAL_FLAG_LABELS[f] || f}</span>)}
    </div>
  );
}

export default function NextPatientCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [next, setNext] = useState(null);
  const [queue, setQueue] = useState([]);
  const [allergies, setAllergies] = useState(undefined); // undefined = loading, null = not available, '' = none recorded
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [calling, setCalling] = useState(false);
  const allergyFor = useRef(null);

  const load = useCallback(async () => {
    const [n, q] = await Promise.allSettled([getNextInQueue(), getClinicQueue()]);
    setNext(n.status === 'fulfilled' ? n.value : null);
    setQueue(q.status === 'fulfilled' ? q.value : []);
    setError(n.status === 'rejected' && q.status === 'rejected');
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // Allergies are not on QueueEntry; look them up for the patient being shown.
  const nextPatientId = next?.patientId;
  useEffect(() => {
    if (!nextPatientId || allergyFor.current === nextPatientId) return;
    allergyFor.current = nextPatientId;
    setAllergies(undefined);
    getPatientAllergies(nextPatientId, user?.email)
      .then(setAllergies)
      .catch(() => setAllergies(null));
  }, [nextPatientId, user?.email]);

  const callIn = async () => {
    setCalling(true);
    try {
      await startConsultation(next.appointmentId);
      navigate(`/doctor/appointments/${next.appointmentId}`);
    } catch (err) {
      toast.error(err?.response?.status === 409
        ? 'That patient is no longer waiting — refreshing the queue'
        : "Couldn't start the consultation — please try again");
      load();
      setCalling(false);
    }
  };

  const waiting = queue.filter(e => e.status === 'ARRIVED');
  const inRoom = queue.filter(e => e.status === 'IN_CONSULTATION');

  return (
    <section className="card fade-in-up delay-2" aria-label="Next patient" data-testid="next-patient-card">
      <div className="card-title">Next patient</div>

      {loading ? <div className="skeleton skeleton--row" /> : error ? (
        <div className="alert alert-error">
          <div className="grow">Couldn't load the waiting room.</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>Retry</button>
        </div>
      ) : !next ? (
        <div className="empty-state">
          <div className="empty-title">No one is waiting</div>
          <div className="empty-desc">Patients appear here once reception checks them in.</div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            {next.token && <span className="badge badge-purple mono">{next.token}</span>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="card-row-title">{next.patientName}</div>
              <div className="card-row-meta">Waiting {formatWaiting(next.waitingMinutes)}</div>
            </div>
          </div>
          {allergies && (
            <div className="alert alert-warning" role="note"><div className="grow"><strong>Allergies:</strong> {allergies}</div></div>
          )}
          {allergies === null && (
            <div className="alert alert-info" role="note"><div className="grow"><strong>Allergies:</strong> not available — check the patient record.</div></div>
          )}
          {allergies === '' && (
            <div className="alert alert-info" role="note"><div className="grow"><strong>Allergies:</strong> No allergies recorded</div></div>
          )}
          <div className="field-group">
            <div className="field-label">Complaint</div>
            <div className="field-text">{next.reason || 'Not recorded'}</div>
          </div>
          <div className="field-group">
            <div className="field-label">Latest vitals</div>
            {next.vitalsRecorded === false && !next.latestVitals
              ? <span className="card-row-meta">Vitals not taken yet</span>
              : <VitalsLine vitals={next.latestVitals} />}
          </div>
          <button className="btn btn-accent" disabled={calling} onClick={callIn}>
            {calling ? 'Calling in…' : 'Call in & open consultation'}
          </button>
        </>
      )}

      {!loading && !error && (waiting.length > 0 || inRoom.length > 0) && (
        <div style={{ marginTop: 18 }}>
          <div className="field-label">Waiting room ({waiting.length})</div>
          <div className="card-list">
            {[...inRoom, ...waiting].map(e => (
              <button key={e.appointmentId} type="button" className="card-row"
                onClick={() => navigate(`/doctor/appointments/${e.appointmentId}`)}
                aria-label={`Open ${e.patientName}`}>
                <div className="card-row-body">
                  <div className="card-row-title">{e.token ? `${e.token} · ` : ''}{e.patientName}</div>
                  <div className="card-row-meta">
                    {e.status === 'IN_CONSULTATION' ? 'In consultation' : `Waiting ${formatWaiting(e.waitingMinutes)}`}
                    {e.reason ? ` · ${e.reason}` : ''}
                  </div>
                </div>
                <span className={`badge ${e.status === 'IN_CONSULTATION' ? 'badge-purple' : 'badge-pending'}`}>
                  {e.status === 'IN_CONSULTATION' ? 'In room' : 'Waiting'}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
